/* eslint-disable */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import {
    cloneDeep,
    find,
    findIndex,
    forEach,
    includes,
    isEmpty,
    isEqual,
    isNumber,
    isUndefined,
    keyBy,
    merge,
    noop,
    orderBy,
    pick,
    reject,
    remove,
    size,
    some,
    sortBy,
    unionBy,
    uniqBy,
} from 'lodash';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { GroupId } from 'admin/groups/Groups';
import { ModuleId } from 'admin/modules/Modules';
import { useCurrentContext, useProfile } from 'auth/Auth';
import { FilterPicker } from 'common/filters/filter-bar/filter-picker/FilterPicker';
import { SelectedFilter } from 'common/filters/filter-bar/selected-filter/SelectedFilter';
import { toDateRange } from 'common/filters/filter-bar/custom-date-filter/CustomDateFilter'; // eslint-disable-line
import { FiltersActionType, getFilterDefinitions, getFilterValues, useFilters } from 'common/filters/Filters';
import { DataType, FilterDefinition, FilterValue, ObjectModel } from 'common/filters/Filters.types';
import { Overlay } from 'core/overlay/Overlay';
import { CUSTOM_DATE_DELIMITER, Period } from 'core/util/DateRange';
import { usePrevious } from 'core/util/Hook';
import { Filter as SearchFilter } from 'core/util/Search';

import 'common/filters/filter-bar/FilterBar.scss';
import { useIsMobile } from 'tickets/contexts/MobileContext';

export interface FilterBarOptions {
    objectModel: ObjectModel;
    groupID: GroupId;
    moduleIDs?: ModuleId[];
    selectedFilters?: SearchFilter[];
    excluded?: string[];
    depth?: number;
    maxPageSize?: number;
    persist?: boolean;
    includeAll?: boolean; // Includes "all" flag in each selected filter
    savedViewsEnabled?: boolean;
    watchOptionChanges?: boolean; // Watch for changes to filterBarOptions and fetch new filters
    post?: boolean;
    labels?: string[];
    useBackendDefaults?: boolean; // When false, won't apply backend-default selected filters when selectedFilters is empty
}

export interface Props {
    filterBarOptions: FilterBarOptions;
    displayHideOption?: boolean;
    filterOverlayArea?: string;
    renderView?: any;
    enableKeywordSearch?: boolean; // enables keyword search as filter
    showSelectAll?: boolean;
    setAdditionalFilters?: (filterDefinitions: FilterDefinition[]) => void;
    isFilterPickerDisabled?: boolean;
    resetFilters?: boolean;
}

type SelectedFilterDefinition = FilterDefinition & {
    highlight?: boolean;
    all?: boolean; // Implicit "all" - user clicked outside without selecting any values
    allExplicit?: boolean; // Explicit "all" - user manually checked all boxes
};

const PROMISE_AREA = 'filter-bar';

const stripInternalFlags = (filter: SearchFilter): SearchFilter => {
    const { all, allExplicit, ...rest } = filter as any;
    return rest as SearchFilter;
};

const stripInternalFlagsFromList = (filters: SearchFilter[]): SearchFilter[] => {
    return filters.map(stripInternalFlags);
};

export const FilterBar: FunctionComponent<Props> = ({
    filterBarOptions,
    displayHideOption = true,
    filterOverlayArea = PROMISE_AREA,
    renderView = noop,
    enableKeywordSearch = false,
    showSelectAll = false,
    setAdditionalFilters,
    isFilterPickerDisabled = false,
    resetFilters,
}) => {
    const { t } = useTranslation(['common/filters']);
    const isMobile = useIsMobile();
    const isFirstRun = useRef(true); // Track first render

    const [{ profile }] = useProfile();
    const { agencyId, tenantId } = useCurrentContext();

    const [filtersState, filtersDispatch] = useFilters();

    const [collapsed, setCollapsed] = useState(false);
    const [triggered, setTriggered] = useState(false);
    let retried = false;

    const [filterDefinitions, setFilterDefinitions] = useState<FilterDefinition[]>([]);
    const [selectedFilterDefinitions, setSelectedFilterDefinitions] = useState<SelectedFilterDefinition[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<SearchFilter[]>([]);
    const [highlightIndex, setHighlightIndex] = useState(-1);

    const [pendingFocusFilterName, setPendingFocusFilterName] = useState<string | null>(null);
    const selectedFilterRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const [key, setKey] = useState(`r4e-filters-${profile?.user.id}-${profile?.user.locale}`);
    const filterBarClassNames = classnames('r4e-filter-bar', {
        collapsed,
        triggered,
    });

    const previousOptions = usePrevious(filterBarOptions);
    const isInternalUpdate = useRef(false);
    const workingFilterDefinitions = useRef<FilterDefinition[]>([]);
    const workingSelectedFilterDefinitions = useRef<SelectedFilterDefinition[]>([]);

    useEffect(() => {
        workingFilterDefinitions.current = filterDefinitions;
        workingSelectedFilterDefinitions.current = selectedFilterDefinitions;
    }, [filterDefinitions, selectedFilterDefinitions]);

    useEffect(() => {
        if (
            filterBarOptions.watchOptionChanges &&
            !isUndefined(previousOptions) &&
            !isEqual(filterBarOptions.excluded, previousOptions.excluded)
        ) {
            getFilters(true);
        }
    }, [filterBarOptions, previousOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (profile && agencyId && tenantId && !filterBarOptions.savedViewsEnabled) {
            setKey(`r4e-filters-${profile.user.id}-${profile.user.locale}`);
            getFilters(true);
        }
    }, [profile, agencyId, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (filtersState.loading && !isEmpty(filtersState.selectedFilters) && !isInternalUpdate.current) {
            handleFiltersChangeRequest(filtersState.selectedFilters!);
        }
    }, [filtersState]); // eslint-disable-line react-hooks/exhaustive-deps

    // gets called every time the value of resetFilters property toggles from TicketsFilterFullPage component
    useEffect(() => {
        // Skip the very first run of useEffect
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        // Only call getFilters() function when resetFilters is intentionally passed
        if (!isFirstRun.current && resetFilters !== undefined) {
            getFilters(false, true);
        }
    }, [resetFilters]);

    const handleFiltersChangeRequest = async (
        filters: SearchFilter[],
        includeValues: boolean = false,
    ): Promise<void> => {
        // Mark as internal operation
        isInternalUpdate.current = true;

        // Work with fresh copies from refs (always has latest data)
        const newFilterDefinitions = [...workingFilterDefinitions.current];
        const newSelectedFilterDefinitions = [...workingSelectedFilterDefinitions.current];

        for (let index = 0; index < filters.length; index++) {
            const filter = filters[index];
            let filterDefinition = find(newSelectedFilterDefinitions, { name: filter.name });

            if (filterDefinition) {
                const updatedFilterDefinition = { ...filterDefinition };
                if (isEmpty(filter.values)) {
                    updatedFilterDefinition.values = filterDefinition.values.map((fv) => ({
                        ...fv,
                        selected: false,
                    }));
                } else {
                    if (filter.locked) {
                        updatedFilterDefinition.locked = filter.locked;
                    }
                    updatedFilterDefinition.values = filterDefinition.values.map((fv) => {
                        const newFv = { ...fv, selected: includes(filter.values, fv.value) };

                        if (updatedFilterDefinition.dataType === DataType.Date) {
                            const isCustomDateFilter = includes(filter.values[0], CUSTOM_DATE_DELIMITER);
                            if (isCustomDateFilter && fv.value === Period.Custom) {
                                newFv.selected = true;
                                newFv.dateRange = toDateRange(filter.values[0]);
                            }
                        }

                        return newFv;
                    });
                }

                // CRITICAL: Replace flags (not preserve) - clear old flags if not in new filter
                // This ensures when user changes from "all" to specific values, the all flag is cleared
                if (filter.all) {
                    updatedFilterDefinition.all = true;
                } else {
                    delete updatedFilterDefinition.all;
                }

                if (filter.allExplicit) {
                    updatedFilterDefinition.allExplicit = true;
                } else {
                    delete updatedFilterDefinition.allExplicit;
                }

                const updateIndex = findIndex(newSelectedFilterDefinitions, { name: filterDefinition.name });
                if (updateIndex > -1) {
                    newSelectedFilterDefinitions[updateIndex] = updatedFilterDefinition;
                }
            } else {
                filterDefinition = find(newFilterDefinitions, { name: filter.name });

                if (filterDefinition) {
                    const newFilterDef: SelectedFilterDefinition = { ...filterDefinition };

                    if (filter.locked) {
                        newFilterDef.locked = filter.locked;
                    }

                    // Replace the all/allExplicit flags (not preserve)
                    if (filter.all) {
                        newFilterDef.all = true;
                    } else {
                        delete newFilterDef.all;
                    }

                    if (filter.allExplicit) {
                        newFilterDef.allExplicit = true;
                    } else {
                        delete newFilterDef.allExplicit;
                    }

                    try {
                        if (!newFilterDef.values || newFilterDef.values.length === 0) {
                            const filterValues = await trackPromise(
                                getFilterValues(agencyId, tenantId, newFilterDef.name, {
                                    name: newFilterDef.name,
                                    objectFieldName: newFilterDef.objectFieldName,
                                    moduleIDs: filterBarOptions.moduleIDs,
                                    selectedFilters: toSearchFilters(newSelectedFilterDefinitions),
                                }),
                                filterOverlayArea,
                            );
                            newFilterDef.values = filterValues.values;
                        }

                        newFilterDef.values = newFilterDef.values.map((fv) => ({
                            ...fv,
                            selected: includes(filter.values, fv.value),
                        }));

                        const defIndex = findIndex(newFilterDefinitions, { name: newFilterDef.name });
                        if (defIndex > -1) {
                            newFilterDefinitions[defIndex] = {
                                ...newFilterDefinitions[defIndex],
                                selected: true,
                                values: newFilterDef.values,
                            };
                        }

                        newSelectedFilterDefinitions.push(newFilterDef);
                    } catch {
                        console.error('Error processing filter values for:', filter.name);
                    }
                }
            }
        }
        // Orphaned filter detection
        const currentSearchFilters = toSearchFilters(newSelectedFilterDefinitions);
        const orphanedFilters = findOrphanedDependentFilters(currentSearchFilters);

        if (orphanedFilters.length > 0) {
            orphanedFilters.forEach((orphanedFilterName) => {
                const filterIndex = findIndex(newSelectedFilterDefinitions, { name: orphanedFilterName });
                if (filterIndex > -1) {
                    newSelectedFilterDefinitions.splice(filterIndex, 1);
                }

                const defIndex = findIndex(newFilterDefinitions, { name: orphanedFilterName });
                if (defIndex > -1) {
                    newFilterDefinitions[defIndex] = {
                        ...newFilterDefinitions[defIndex],
                        selected: false,
                        values: newFilterDefinitions[defIndex].values?.map((fv) => ({
                            ...fv,
                            selected: false,
                        })),
                    };
                }
            });
        }

        const { length } = newSelectedFilterDefinitions;
        const searchFilters = toSearchFilters(newSelectedFilterDefinitions);

        await refreshFilters(
            length,
            searchFilters,
            newSelectedFilterDefinitions,
            length - 1,
            true,
            includeValues,
            newFilterDefinitions,
        );
        // Update refs immediately so next call has fresh data
        workingFilterDefinitions.current = newFilterDefinitions;
        workingSelectedFilterDefinitions.current = newSelectedFilterDefinitions;

        if (!includeValues) {
            // For normal filter changes, update filterDefinitions from our local copy
            workingFilterDefinitions.current = newFilterDefinitions;
            setFilterDefinitions(newFilterDefinitions);
        }
        setSelectedFilterDefinitions(orderBy(newSelectedFilterDefinitions, 'order'));
        setSelectedFilters(searchFilters);

        isInternalUpdate.current = false;
    };

    const findOrphanedDependentFilters = (selectedFilters: SearchFilter[]): string[] => {
        const orphanedFilters: string[] = [];
        const FILTER_DEPENDENCIES: Record<string, { parent: string; requiredValue: string }> = {
            'R4ETicket.ticket.surveyTemplate': {
                parent: 'R4ETicket.ticket.ticketType',
                requiredValue: 'Survey',
            },
            // Can Add other dependencies here as needed
        };
        selectedFilters?.forEach((selectedFilter) => {
            const dependency = FILTER_DEPENDENCIES[selectedFilter.name];
            if (dependency) {
                const parentFilter = selectedFilters.find((sf) => sf.name === dependency.parent);

                if (!parentFilter) {
                    // Parent doesn't exist at all - orphaned
                    orphanedFilters.push(selectedFilter.name);
                } else {
                    // Don't mark as orphaned in this case
                    if (parentFilter.all || isEmpty(parentFilter.values)) {
                        // Parent has "all" selected, dependent filter is valid
                        return;
                    }

                    // Parent has specific values - check if required value is present
                    const hasRequiredValue = parentFilter.values?.some((value) => value === dependency.requiredValue);

                    if (!hasRequiredValue) {
                        orphanedFilters.push(selectedFilter.name);
                    }
                }
            }
        });

        return orphanedFilters;
    };

    const toSearchFilters = (filterDefns: SelectedFilterDefinition[]): SearchFilter[] => {
        const searchFilters = filterDefns.map((filterDefinition) => {
            const selectedFilterValues = filterDefinition.values.filter((filterValue) => filterValue.selected);

            // Check if the filterDefinition already has these flags (from handleFiltersChangeRequest)
            const hasAllFlag = filterDefinition.all === true;
            const hasAllExplicitFlag = filterDefinition.allExplicit === true;

            // NO values selected = user clicked outside = implicit "all" = send empty array
            const noValuesSelected = selectedFilterValues.length === 0;

            // ALL values explicitly selected = user checked all boxes = send all values
            const allValuesExplicitlySelected =
                selectedFilterValues.length > 0 && selectedFilterValues.length === filterDefinition.values.length;

            // Use existing flags if present, otherwise compute them
            const isImplicitAll = hasAllFlag || (noValuesSelected && !hasAllExplicitFlag);
            const isExplicitAll = hasAllExplicitFlag || (allValuesExplicitlySelected && !hasAllFlag);

            const result: any = {
                name: filterDefinition.name,
                values: isImplicitAll
                    ? [] // Implicit "all" - send empty array
                    : selectedFilterValues.map((filterValue) => {
                          return filterValue.dateRange ? filterValue.dateRange.value : filterValue.value;
                      }), // Explicit selection - send actual values (even if all are selected)
                labels: isImplicitAll
                    ? []
                    : selectedFilterValues.map((filterValue) => {
                          return filterValue.dateRange ? filterValue.dateRange.label : filterValue.label;
                      }),
            };

            // Only include these properties if they're true (for backwards compatibility)
            if (filterDefinition.locked) {
                result.locked = filterDefinition.locked;
            }
            if (isImplicitAll) {
                result.all = true;
            }
            if (isExplicitAll) {
                result.allExplicit = true;
            }

            return result as SearchFilter;
        });

        return orderBy(searchFilters, 'order').filter(
            (filter) => !isEmpty(filter.values) || filter.locked || filter.all,
        );
    };

    const setAllFlag = (selectedFilters: SearchFilter[]) => {
        const setFlag = (filter: SearchFilter) => {
            const definition = find(filterDefinitions, { name: filter.name });

            if (filter.values?.length === definition?.values?.length) {
                return { ...filter, all: true };
            }

            return { ...filter, all: false };
        };

        return selectedFilters.map(setFlag);
    };

    const getFilters = (
        initial?: boolean,
        reset?: boolean,
        searchFilters?: SearchFilter[],
        refresh?: boolean,
    ): void => {
        const request = {
            filterDefinitionID: key,
            moduleGroup: filterBarOptions.groupID,
            objectModelName: filterBarOptions.objectModel,
            selectedFilters: filterBarOptions.selectedFilters,
            excluded: filterBarOptions.excluded,
            depth: filterBarOptions.depth,
            maxPageSize: filterBarOptions.maxPageSize,
        };
        const args = merge(
            { key, includeValues: initial || reset },
            pick(filterBarOptions, ['moduleIDs', 'disabled', 'removed']),
        );
        let newSelectedFilters: SearchFilter[] = [];

        if (initial) {
            newSelectedFilters = getInitialSelectedFilters();
        } else if (reset) {
            const storedFilters = localStorage.getItem(key + '-initial');
            const storeFilterDefinitionsNames: string[] = [];
            if (storedFilters) {
                const storedFiltersByKey: { [key: string]: any } = JSON.parse(storedFilters);

                forEach(storedFiltersByKey, (filter) => {
                    storeFilterDefinitionsNames.push(filter.name);
                    newSelectedFilters.push(filter);
                });

                workingSelectedFilterDefinitions.current = workingSelectedFilterDefinitions.current.filter(
                    (filterDef) => includes(storeFilterDefinitionsNames, filterDef.name),
                );
            }

            filtersDispatch({ type: FiltersActionType.ChangeReset, selectedFilters: newSelectedFilters });
        } else if (searchFilters?.length) {
            newSelectedFilters = searchFilters;
        } else {
            newSelectedFilters = toSearchFilters(selectedFilterDefinitions);
        }

        //Strip metadata flags before sending to backend
        request.selectedFilters = stripInternalFlagsFromList(newSelectedFilters);

        if (
            !initial &&
            areFiltersEqual(selectedFilterDefinitions, newSelectedFilters) &&
            filterDefinitions.length &&
            !refresh
        ) {
            return;
        }

        (async (): Promise<any> => {
            try {
                const filterDefns = await trackPromise(
                    getFilterDefinitions(agencyId, tenantId, request, args, filterBarOptions?.post ?? true),
                    filterOverlayArea,
                );

                // If useBackendDefaults is false and no filters are explicitly selected,
                // clear all backend pre-selections so nothing is shown as selected
                // and all filters remain available in the FilterPicker list
                if (filterBarOptions.useBackendDefaults === false && isEmpty(newSelectedFilters)) {
                    filterDefns.forEach((defn) => {
                        defn.selected = false;
                        if (defn.values) {
                            defn.values.forEach((v) => {
                                v.selected = false;
                            });
                        }
                    });
                }

                if (setAdditionalFilters) {
                    setAdditionalFilters(filterDefns);
                }

                const filterDefnNames = filterDefns.map((filterDefn) => filterDefn.name);
                let selectedFilterDefns = filterDefns.filter(
                    (filterDefinition) => filterDefinition.selected,
                ) as SelectedFilterDefinition[];

                remove(newSelectedFilters, (filter) => {
                    return !includes(filterDefnNames, filter.name);
                });

                if (
                    !areFiltersEqual(selectedFilterDefinitions, newSelectedFilters) ||
                    (initial && !areFiltersEqual(selectedFilterDefns, newSelectedFilters))
                ) {
                    const filterDefinitionsByName = keyBy(filterDefinitions, 'name');

                    selectedFilterDefns.forEach((filterDefinition) => {
                        const selectedDateFilterExists = request.selectedFilters?.filter(
                            (filter) => filter.name === 'date',
                        );
                        const isCustomAndSelectedDateFilterExists =
                            selectedDateFilterExists?.[0]?.values?.[0] &&
                            includes(selectedDateFilterExists[0].values[0], CUSTOM_DATE_DELIMITER);

                        if (isCustomAndSelectedDateFilterExists && selectedDateFilterExists?.[0]?.values?.[0]) {
                            if (
                                filterDefinition.dataType === DataType.Date &&
                                (!filterDefinition?.selectedValues || filterDefinition?.selectedValues.length === 0)
                            ) {
                                filterDefinition.values.forEach((filterValue) => {
                                    /* eslint-disable no-param-reassign */
                                    if (filterValue.value === Period.Custom) {
                                        filterValue.selected = true;
                                        filterValue.dateRange = toDateRange(selectedDateFilterExists[0].values[0]);
                                    }
                                    /* eslint-enable no-param-reassign */
                                });
                            }
                        }

                        if (!filterDefinition.values && filterDefinitionsByName[filterDefinition.name]) {
                            /* eslint-disable no-param-reassign */
                            filterDefinition.values = filterDefinitionsByName[filterDefinition.name].values;
                            filterDefinition.valuesCount = filterDefinitionsByName[filterDefinition.name].valuesCount;
                            /* eslint-enable no-param-reassign */
                        }

                        const matchingFilter = newSelectedFilters.find(
                            (filter) => filter.name === filterDefinition.name,
                        );

                        const isLocked = matchingFilter?.locked;

                        if (isLocked) {
                            /* eslint-disable no-param-reassign */
                            filterDefinition.locked = isLocked;
                        }

                        // CRITICAL: Preserve all/allExplicit flags from localStorage
                        if (matchingFilter?.all) {
                            /* eslint-disable no-param-reassign */
                            filterDefinition.all = true;

                            // CRITICAL: When all=true, unselect all values to show as "All" with no checkboxes
                            if (filterDefinition.values) {
                                filterDefinition.values.forEach((fv) => {
                                    fv.selected = false;
                                });
                            }
                            /* eslint-enable no-param-reassign */
                        }
                        if (matchingFilter?.allExplicit) {
                            /* eslint-disable no-param-reassign */
                            filterDefinition.allExplicit = true;
                            /* eslint-enable no-param-reassign */
                        }
                    });

                    if (initial) {
                        if (isEmpty(newSelectedFilters)) {
                            // Extract search filters from values returned by backend
                            newSelectedFilters = toSearchFilters(selectedFilterDefns);
                        } else {
                            // Preserve order from saved filters
                            selectedFilterDefns = sortBy(selectedFilterDefns, (filterDefn) => {
                                const index = findIndex(newSelectedFilters, { name: filterDefn.name });

                                if (index === -1) {
                                    return selectedFilterDefns.length;
                                }
                                return index;
                            });

                            // Handle saved custom date filters
                            const dateFilterNames = filterDefns
                                .filter((filterDefn) => filterDefn.dataType === DataType.Date)
                                .map((filterDefn) => filterDefn.name);
                            const customDateFilters = newSelectedFilters.filter((filter) => {
                                return (
                                    includes(dateFilterNames, filter.name) &&
                                    includes(filter.values[0], CUSTOM_DATE_DELIMITER)
                                );
                            });
                            const customDateFiltersByName = keyBy(customDateFilters, 'name');

                            if (!isEmpty(customDateFilters)) {
                                selectedFilterDefns.forEach((filterDefinition) => {
                                    if (customDateFiltersByName[filterDefinition.name]) {
                                        filterDefinition.values.forEach((filterValue) => {
                                            /* eslint-disable no-param-reassign */
                                            if (filterValue.value === Period.Custom) {
                                                filterValue.selected = true;
                                                filterValue.dateRange = toDateRange(
                                                    customDateFiltersByName[filterDefinition.name].values[0],
                                                );
                                            }
                                            /* eslint-enable no-param-reassign */
                                        });
                                    }
                                });
                            }
                            newSelectedFilters = toSearchFilters(selectedFilterDefns);
                        }
                    }

                    if (reset && isEmpty(newSelectedFilters)) {
                        // Use default values returned by backend
                        newSelectedFilters = toSearchFilters(selectedFilterDefns);
                    }

                    setSelectedFilterDefinitions([...orderBy(selectedFilterDefns, 'order')]);
                }

                setSelectedFilters(newSelectedFilters);
                setFilterDefinitions(filterDefns || []);

                if (filterBarOptions.includeAll) {
                    newSelectedFilters = setAllFlag(newSelectedFilters);
                }
                saveSelectedFilters(newSelectedFilters, reset);
                // Filter out implicit "all" for parent component
                const filtersForParent = newSelectedFilters.filter(
                    (filter) => !isEmpty(filter.values) || filter.locked,
                );

                filtersDispatch({
                    type: FiltersActionType.ChangeSuccess,
                    selectedFilters: filtersForParent,
                });
            } catch {
                filtersDispatch({ type: FiltersActionType.ChangeError });

                if (initial && !retried) {
                    retried = true;
                    getFilters(false, true);
                }
            }
        })();
    };

    const getInitialSelectedFilters = (): SearchFilter[] => {
        const storedFilters = localStorage.getItem(key);
        let initialFilters: SearchFilter[] = [];

        if (filterBarOptions.persist && storedFilters) {
            let storedInitialFilters: SearchFilter[] = [];
            const storedFiltersByKey: { [key: string]: any } = JSON.parse(storedFilters);

            forEach(storedFiltersByKey, (filter) => {
                storedInitialFilters.push(filter);
            });

            storedInitialFilters = uniqBy(orderBy(storedInitialFilters, 'order'), 'name').map((filter) => {
                let initialFilter: any = {
                    name: filter.name,
                    values: filter.values,
                    all: filter.all,
                    allExplicit: filter.allExplicit,
                };

                if (filter?.locked) {
                    initialFilter = { ...initialFilter, locked: filter.locked };
                }

                return initialFilter;
            });

            filterBarOptions.selectedFilters?.forEach((filter, index) => {
                if (!find(storedInitialFilters, { name: filter.name })) {
                    initialFilters.push(filter);
                }
            });
            initialFilters = initialFilters.concat(storedInitialFilters);
        } else if (filterBarOptions.selectedFilters) {
            initialFilters = filterBarOptions.selectedFilters;
        }

        return initialFilters;
    };

    const saveSelectedFilters = (selectedFilters: SearchFilter[], reset?: boolean): void => {
        if (!filterBarOptions.persist) {
            return;
        }

        const storedLockedFilters = localStorage.getItem(key + '-initial')
            ? cloneDeep(JSON.parse(localStorage.getItem(key + '-initial')!))
            : {};

        if (reset) {
            localStorage.removeItem(key);
            localStorage.setItem(key, JSON.stringify(storedLockedFilters));
            // return;
        }

        const storedFilters = localStorage.getItem(key) ? cloneDeep(JSON.parse(localStorage.getItem(key)!)) : {};
        const filterNames = filterDefinitions.map((filterDefinition) => filterDefinition.name);
        const filtersByName: { [key: string]: any } = {};

        selectedFilters.forEach((filter, index) => {
            filtersByName[filter.name] = { ...filter, order: index };
            if (filter.locked && !storedLockedFilters[filter.name]) {
                storedLockedFilters[filter.name] = filter;
            }
        });

        filterNames.forEach((filterName) => {
            if (filtersByName[filterName]) {
                storedFilters[filterName] = filtersByName[filterName];
            } else {
                delete storedFilters[filterName];
            }
        });

        if (Object.keys(storedFilters).length > 0) {
            localStorage.setItem(key, JSON.stringify(storedFilters));
        }

        if (Object.keys(storedLockedFilters).length > 0) {
            localStorage.setItem(key + '-initial', JSON.stringify(storedLockedFilters));
        }
    };

    const areFiltersEqual = (currentFilters: FilterDefinition[], updatedFilters: SearchFilter[]): boolean => {
        if (size(currentFilters) !== size(updatedFilters)) {
            return false;
        }

        let filtersEqual = true;
        let currentFilter;
        let currentFilterValues;
        let updatedFilter;

        for (let i = 0, fl = size(currentFilters); i < fl; ++i) {
            currentFilter = currentFilters[i];
            updatedFilter = updatedFilters[i];
            currentFilterValues = !currentFilter.values
                ? []
                : currentFilter.values.filter((filterValue) => filterValue.selected);

            if (currentFilter.name !== updatedFilter.name || size(currentFilterValues) !== size(updatedFilter.values)) {
                filtersEqual = false;
            }

            for (let j = 0, vl = size(currentFilterValues); j < vl; ++j) {
                if (currentFilterValues[j].value !== updatedFilter.values[j]) {
                    filtersEqual = false;
                }
            }
        }

        return filtersEqual;
    };

    const toggleFilters = (collapse: boolean): void => {
        setCollapsed(collapse);
        setTriggered(true);

        // TODO - resize?
    };

    const refreshFilters = async (
        startIndex: number,
        filters: SearchFilter[],
        selectedFilterDefns: SelectedFilterDefinition[],
        highlightIndex?: number,
        shouldDispatch = true,
        includeValues = false,
        currentFilterDefinitions?: FilterDefinition[],
    ): Promise<any> => {
        let searchFilters = [...filters];
        const updatedSelectedFilterDefns = [...selectedFilterDefns];

        //Use passed definitions or fall back to ref
        const activeFilterDefinitions = currentFilterDefinitions || workingFilterDefinitions.current;

        try {
            const request = {
                filterDefinitionID: key,
                moduleGroup: filterBarOptions.groupID,
                objectModelName: filterBarOptions.objectModel,
                //Strip internal flags before backend call
                selectedFilters: includeValues
                    ? searchFilters.map((f) => ({ name: f.name, values: f.values }))
                    : stripInternalFlagsFromList(searchFilters),
                excluded: filterBarOptions.excluded,
            };

            const args = merge({ key, includeValues }, pick(filterBarOptions, ['moduleIDs', 'disabled', 'removed']));

            const newFilterDefns = await trackPromise(
                getFilterDefinitions(agencyId, tenantId, request, args, filterBarOptions?.post ?? true),
                filterOverlayArea,
            );

            // Use activeFilterDefinitions instead of stale filterDefinitions
            const oldFilterDefnNames = activeFilterDefinitions.map((filterDefn) => filterDefn.name).sort();
            const newFilterDefnNames = newFilterDefns.map((filterDefn: FilterDefinition) => filterDefn.name).sort();
            const promises: Promise<any>[] = [];
            let selectedFilterDefnsChanged = false;

            updatedSelectedFilterDefns.forEach((selectedFilterDefinition, i) => {
                if (i >= startIndex && includes(newFilterDefnNames, selectedFilterDefinition.name)) {
                    promises.push(
                        getFilterValues(agencyId, tenantId, selectedFilterDefinition.name, {
                            name: selectedFilterDefinition.name,
                            objectFieldName: selectedFilterDefinition.objectFieldName,
                            moduleIDs: filterBarOptions.moduleIDs,
                            // Strip internal flags before backend call
                            selectedFilters: includeValues
                                ? searchFilters.slice(0, i).map((f) => ({ name: f.name, values: f.values }))
                                : stripInternalFlagsFromList(searchFilters.slice(0, i)),
                        }),
                    );
                } else {
                    promises.push(Promise.resolve());
                }
            });

            const results = await trackPromise(Promise.all(promises), filterOverlayArea);
            let filterValues: string[];

            results.forEach((result, i) => {
                if (result) {
                    filterValues = result.values.map((filterValue: FilterValue) => filterValue.value);

                    if (searchFilters[i]) {
                        // Create new values array with updated selection
                        // When filter.all is true (user clicked outside), keep all checkboxes UNCHECKED
                        // When filter.allExplicit is true (user checked all boxes), mark all as selected
                        const updatedValues = result.values.map((value: FilterValue) => ({
                            ...value,
                            selected: searchFilters[i].all
                                ? false // Implicit all - no checkboxes
                                : includes(searchFilters[i].values, value.value), // Explicit selection
                        }));

                        // Filter out values that no longer exist
                        const validValues = searchFilters[i].values.filter((value) => includes(filterValues, value));
                        searchFilters[i] = { ...searchFilters[i], values: validValues };

                        // Update the filter definition with new values
                        updatedSelectedFilterDefns[i] = {
                            ...updatedSelectedFilterDefns[i],
                            values: updatedValues,
                            valuesCount: result.count,
                        };
                        selectedFilterDefnsChanged = true;
                    }
                }
            });

            let removed: SelectedFilterDefinition[] = [];

            if (!isEqual(oldFilterDefnNames, newFilterDefnNames)) {
                const filterDefns = unionBy(
                    [],
                    activeFilterDefinitions.filter((f) => includes(newFilterDefnNames, f.name)),
                    newFilterDefns,
                    'name',
                );

                removed = remove(updatedSelectedFilterDefns, (filterDefinition) => {
                    return !includes(newFilterDefnNames, filterDefinition.name);
                });

                selectedFilterDefnsChanged = !isEmpty(removed);
                // Update both state and ref
                workingFilterDefinitions.current = filterDefns;
                setFilterDefinitions(filterDefns);
            }

            if (isNumber(highlightIndex) && highlightIndex > -1) {
                setHighlightIndex(highlightIndex);
                setTimeout(() => setHighlightIndex(-1), 5000);
            }

            if (selectedFilterDefnsChanged) {
                // Update ref immediately
                workingSelectedFilterDefinitions.current = updatedSelectedFilterDefns;
                setSelectedFilterDefinitions(orderBy(updatedSelectedFilterDefns, 'order'));
                let searchFiltersWithoutRemovedDefns = reject(searchFilters, (filter) =>
                    some(removed, { name: filter.name }),
                );

                if (filterBarOptions.includeAll) {
                    searchFiltersWithoutRemovedDefns = setAllFlag(searchFiltersWithoutRemovedDefns);
                }

                setSelectedFilters(searchFiltersWithoutRemovedDefns);
                saveSelectedFilters(searchFiltersWithoutRemovedDefns);

                if (shouldDispatch) {
                    const filtersForParent = searchFiltersWithoutRemovedDefns.filter(
                        (filter) => !isEmpty(filter.values) || filter.locked,
                    );
                    filtersDispatch({
                        type: FiltersActionType.ChangeSuccess,
                        selectedFilters: filtersForParent,
                    });
                }
            } else {
                if (filterBarOptions.includeAll) {
                    searchFilters = setAllFlag(searchFilters);
                }

                setSelectedFilters(searchFilters);
                saveSelectedFilters(searchFilters);

                if (shouldDispatch) {
                    const filtersForParent = searchFilters.filter((filter) => !isEmpty(filter.values) || filter.locked);
                    filtersDispatch({
                        type: FiltersActionType.ChangeSuccess,
                        selectedFilters: filtersForParent,
                    });
                }
            }
        } catch (error) {
            filtersDispatch({ type: FiltersActionType.ChangeError });
        }
    };

    const addFilter = async (filterDefinition: FilterDefinition, shouldDispatch?: boolean): Promise<any> => {
        // CRITICAL: Check if filter has any values at all
        if (!filterDefinition.values || filterDefinition.values.length === 0) {
            return; // Don't add filters with no values available
        }

        const selectedValues = filterDefinition.values.filter((fv) => fv.selected);
        const noValuesSelected = selectedValues.length === 0;
        const allValuesExplicitlySelected =
            selectedValues.length > 0 && selectedValues.length === filterDefinition.values.length;

        // Distinguish between clicking outside (implicit all) vs checking all boxes (explicit all)
        const isImplicitAll = noValuesSelected;
        const isExplicitAll = allValuesExplicitlySelected;

        // Prepare the new filter as a SearchFilter
        const newSearchFilter: SearchFilter = {
            name: filterDefinition.name,
            // Implicit all: send empty array. Explicit all: send all values
            values: isImplicitAll ? [] : selectedValues.map((fv) => (fv.dateRange ? fv.dateRange.value : fv.value)),
            locked: filterDefinition.locked,
            labels: isImplicitAll ? [] : selectedValues.map((fv) => (fv.dateRange ? fv.dateRange.label : fv.label)),
            all: isImplicitAll, // Only set "all" flag for implicit all
            allExplicit: isExplicitAll, // Track explicit selection of all values
        };

        // Route through batch handler
        await handleFiltersChangeRequest([newSearchFilter], true);
    };

    const handleAddFilterFromPicker = async (filterDefinition: FilterDefinition): Promise<any> => {
        setPendingFocusFilterName(filterDefinition.name);
        return addFilter(filterDefinition);
    };

    const updateFilter = async (filterDefinition: FilterDefinition, highlight?: boolean): Promise<any> => {
        const selectedValues = filterDefinition.values.filter((fv) => fv.selected);
        const noValuesSelected = selectedValues.length === 0;
        const allValuesExplicitlySelected =
            selectedValues.length > 0 && selectedValues.length === filterDefinition.values.length;

        // Determine the state
        const isImplicitAll = noValuesSelected;
        const isExplicitAll = allValuesExplicitlySelected;

        // Convert FilterDefinition to SearchFilter format
        const updatedSearchFilter: SearchFilter = {
            name: filterDefinition.name,
            values: isImplicitAll ? [] : selectedValues.map((fv) => (fv.dateRange ? fv.dateRange.value : fv.value)),
            locked: filterDefinition.locked,
        };

        // Set flags based on selection state
        if (isImplicitAll) {
            updatedSearchFilter.all = true;
        } else if (isExplicitAll) {
            updatedSearchFilter.allExplicit = true;
        }
        // If neither (specific values selected), don't set any flags - this clears previous all/allExplicit

        // Route through batch handler for consistency
        await handleFiltersChangeRequest([updatedSearchFilter], true);
    };

    const removeFilter = (filterDefinition: FilterDefinition): void => {
        const i = findIndex(filterDefinitions, { name: filterDefinition.name });
        const j = findIndex(selectedFilterDefinitions, { name: filterDefinition.name });

        filterDefinitions[i].selected = false;
        filterDefinitions[i].values.forEach((filterValue) => {
            // eslint-disable-next-line no-param-reassign
            filterValue.selected = false;
        });

        selectedFilterDefinitions.splice(j, 1);

        // Check for orphaned dependent filters after removing the target filter
        const tempSearchFilters = toSearchFilters(selectedFilterDefinitions);
        const orphanedFilters = findOrphanedDependentFilters(tempSearchFilters);

        if (orphanedFilters.length > 0) {
            orphanedFilters.forEach((orphanedFilterName) => {
                const filterIndex = findIndex(selectedFilterDefinitions, { name: orphanedFilterName });
                if (filterIndex > -1) {
                    selectedFilterDefinitions.splice(filterIndex, 1);
                }

                const defIndex = findIndex(filterDefinitions, { name: orphanedFilterName });
                if (defIndex > -1) {
                    filterDefinitions[defIndex].selected = false;
                    if (filterDefinitions[defIndex].values) {
                        filterDefinitions[defIndex].values.forEach((fv) => {
                            // eslint-disable-next-line no-param-reassign
                            fv.selected = false;
                        });
                    }
                }
            });
        }

        const searchFilters = toSearchFilters(selectedFilterDefinitions);

        // Keep the existing behavior exactly as before
        refreshFilters(j, searchFilters, selectedFilterDefinitions, undefined, true, true, filterDefinitions);
    };

    return (
        <div className={filterBarClassNames}>
            <Overlay area={filterOverlayArea} />
            {!collapsed && (
                <div className="collapse-container">
                    <div className="filters-container">
                        {renderView(getFilters)}
                        {selectedFilterDefinitions.map((selectedFilterDefinition, index) => (
                            <div
                                key={`selected-filter-${selectedFilterDefinition.objectFieldName}`}
                                ref={(node) => {
                                    selectedFilterRefs.current[selectedFilterDefinition.name] = node;
                                }}
                                data-selected-filter-name={selectedFilterDefinition.name}
                            >
                                <SelectedFilter
                                    key={`selected-filter-${selectedFilterDefinition.objectFieldName}`}
                                    className={classnames({ highlight: index === highlightIndex })}
                                    filter={selectedFilterDefinition}
                                    onChange={updateFilter}
                                    onRemove={removeFilter}
                                    showSelectAll={showSelectAll}
                                    isFilterDisabled={isFilterPickerDisabled}
                                />
                            </div>
                        ))}
                        {filterDefinitions.length ? (
                            <FilterPicker
                                filters={filterDefinitions}
                                moduleIDs={filterBarOptions.moduleIDs}
                                selectedFilters={selectedFilters}
                                onSelect={handleAddFilterFromPicker}
                                reloadFilter={getFilters}
                                overlayArea={filterOverlayArea}
                                filterPickerText={enableKeywordSearch ? t('ADD_FILTER_OR_KEYWORD') : t('ADD_FILTER')}
                                showSelectAll={showSelectAll}
                                isFilterPickerDisabled={isFilterPickerDisabled}
                            />
                        ) : (
                            ''
                        )}
                    </div>
                    {!isMobile && (
                        <div className="filters-actions">
                            {displayHideOption && (
                                <Button variant="link" onClick={(): void => toggleFilters(true)}>
                                    {t('HIDE')}
                                </Button>
                            )}
                            {displayHideOption && <span className="separator">|</span>}
                            <Button
                                disabled={isFilterPickerDisabled}
                                className="reset-link"
                                variant="link"
                                onClick={(): void => getFilters(false, true)}
                            >
                                {t('RESET')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
            {collapsed && (
                <div className="expand-container">
                    <Button className="ml-3" size="sm" variant="primary" onClick={(): void => toggleFilters(false)}>
                        <i className="icon-filter" />{' '}
                        <span>
                            {t('FILTERS', {
                                filterCount: selectedFilters.filter((filter) => filter.name !== 'object_model').length,
                            })}
                        </span>
                    </Button>
                </div>
            )}
        </div>
    );
};
